import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Icon, Switch } from '@repo/ui';
import {
  type ClioReplicationSchedulerConfig,
  type ClioReplicationSchedulerEntity,
} from '@xtnd/web/shared/types/tenantSettingTypes';
import {
  CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS,
  normalizeClioReplicationSchedulerConfig,
} from '@xtnd/web/features/settings/utils';

interface ClioToDynamoReplicationCardProps {
  config?: Partial<ClioReplicationSchedulerConfig> | null;
  onSave: (config: ClioReplicationSchedulerConfig) => Promise<void>;
  isSaving?: boolean;
}

export function ClioToDynamoReplicationCard({
  config,
  onSave,
  isSaving = false,
}: ClioToDynamoReplicationCardProps) {
  const normalizedInitialConfig = useMemo(
    () => normalizeClioReplicationSchedulerConfig(config),
    [config]
  );

  const [draftConfig, setDraftConfig] = useState<ClioReplicationSchedulerConfig>(normalizedInitialConfig);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setDraftConfig(normalizedInitialConfig);
  }, [normalizedInitialConfig]);

  const isDirty = useMemo(
    () => JSON.stringify(draftConfig) !== JSON.stringify(normalizedInitialConfig),
    [draftConfig, normalizedInitialConfig]
  );

  const updateEntity = (entity: ClioReplicationSchedulerEntity, enabled: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      entities: {
        ...prev.entities,
        [entity]: {
          ...prev.entities[entity],
          replicateToClio: enabled,
        },
      },
    }));
  };

  const handleSave = async () => {
    if (!isDirty || isSaving || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSave(draftConfig);
    } finally {
      setIsSubmitting(false);
    }
  };

  const disabled = isSaving || isSubmitting;

  return (
    <Card padding="lg" variant="outlined">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Icon name="HiDatabase" className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-xtnd-dark-800 dark:text-white">
                Clio API to DynamoDB
              </h3>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
                Manage tenant and entity toggles for Clio replication into DynamoDB.
              </p>
            </div>
          </div>
          <Badge variant={draftConfig.enabled ? 'success' : 'warning'}>
            {draftConfig.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
      </CardHeader>

      <CardBody className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-4">
          <div>
            <p className="text-sm font-semibold text-xtnd-dark-800 dark:text-white">Tenant Replication Toggle</p>
            <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
              Master circuit breaker for Clio API ingestion.
            </p>
          </div>
          <Switch
            checked={draftConfig.enabled}
            onChange={checked => setDraftConfig(prev => ({ ...prev, enabled: checked }))}
            disabled={disabled}
            aria-label="Toggle tenant replication"
          />
        </div>

        <div className={`space-y-3 ${!draftConfig.enabled ? 'opacity-60' : ''}`}>
          {CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS.map(entity => (
            <div
              key={entity.key}
              className="flex items-start justify-between rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-3"
            >
              <div className="pr-4">
                <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">{entity.label}</p>
                <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">{entity.description}</p>
              </div>
              <Switch
                checked={draftConfig.entities[entity.key]?.replicateToClio !== false}
                onChange={checked => updateEntity(entity.key, checked)}
                disabled={disabled || !draftConfig.enabled}
                aria-label={`Toggle ${entity.label} Clio replication`}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!isDirty || disabled} loading={disabled}>
            Save Clio to DynamoDB Configuration
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
